import { useContext, useEffect, useState } from "react";
import { Pencil, Trash } from "react-bootstrap-icons";
import { Navigate, useLocation } from "react-router-dom";
import CustomSpinner from "../components/CustomSpinner";
import UserContext from "../context/UserContext";
import categoryService from "../services/categoryService";
import transactionService from "../services/transactionService";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "MXN",
  }).format(amount ?? 0);

const formatDate = (value) => {
  if (!value) return "-";

  const dateString = value.split("T")[0];
  const date = new Date(dateString.replace(/-/g, "/"));

  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-ES");
};

const getTodayLocal = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().split("T")[0];
};

const getFirstDayOfMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${month}-01`;
};

const toInputDate = (value) => {
  if (!value) return getTodayLocal();
  if (value instanceof Date) return value.toISOString().split("T")[0];
  if (typeof value === "string") {
    return value.includes("T") ? value.split("T")[0] : value;
  }
  return getTodayLocal();
};

const FREQUENCY_LABELS = {
  daily: "Cada día",
  weekly: "Cada semana",
  biweekly: "Cada dos semanas",
  monthly: "Cada mes",
};

const emptyTransaction = () => ({
  categoryId: "",
  categoryName: "",
  amount: "",
  type: "expense",
  description: "",
  date: getTodayLocal(),
  isRecurring: false,
  frequency: "monthly",
});

export default function Transactions() {
  const { user } = useContext(UserContext);
  const location = useLocation();

  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recurringRules, setRecurringRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [filters, setFilters] = useState({
    startDate: getFirstDayOfMonth(),
    endDate: getTodayLocal(),
  });
  const [newTransaction, setNewTransaction] = useState(emptyTransaction());

  const filteredCategories = categories.filter(
    (category) => category.type === newTransaction.type
  );

  const loadTransactions = async (startDate, endDate) => {
    const res = await transactionService.getTransactions(startDate, endDate);
    setTransactions(res.data);
  };

  const loadCategories = async () => {
    const res = await categoryService.getCategories();
    setCategories(res.data);
  };

  const loadRecurringRules = async () => {
    const res = await transactionService.getRecurringTransactions();
    setRecurringRules(res.data);
  };

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        await transactionService.processRecurringTransactions();
        await Promise.all([
          loadCategories(),
          loadTransactions(filters.startDate, filters.endDate),
          loadRecurringRules(),
        ]);
      } catch (error) {
        console.error("Error cargando transacciones:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters.startDate, filters.endDate]);

  useEffect(() => {
    if (location.state?.openForm) {
      setShowForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleFilter = async () => {
    setLoading(true);
    try {
      await loadTransactions(filters.startDate, filters.endDate);
    } catch (error) {
      console.error("Error filtrando transacciones:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetTransactionForm = () => {
    setEditingTransactionId(null);
    setNewTransaction(emptyTransaction());
  };

  const handleOpenCreateForm = () => {
    resetTransactionForm();
    setShowForm(true);
  };

  const handleEditTransaction = (tx) => {
    setEditingTransactionId(tx.id);
    setNewTransaction({
      categoryId: tx.categoryId || "",
      categoryName: tx.categoryName || "",
      amount: tx.amount ?? "",
      type: tx.type || "expense",
      description: tx.description || "",
      date: toInputDate(tx.date),
      isRecurring: false,
      frequency: "monthly",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!newTransaction.categoryId || !newTransaction.amount) {
      alert("Complete los campos requeridos.");
      return;
    }

    setLoading(true);
    try {
      if (editingTransactionId) {
        await transactionService.updateTransaction(editingTransactionId, {
          categoryId: newTransaction.categoryId,
          amount: newTransaction.amount,
          type: newTransaction.type,
          description: newTransaction.description,
          date: newTransaction.date,
        });
      } else if (newTransaction.isRecurring) {
        await transactionService.createRecurringTransaction({
          categoryId: newTransaction.categoryId,
          amount: newTransaction.amount,
          type: newTransaction.type,
          description: newTransaction.description,
          date: newTransaction.date,
          frequency: newTransaction.frequency,
        });
        await transactionService.processRecurringTransactions();
        await loadRecurringRules();
      } else {
        await transactionService.createTransaction({
          categoryId: newTransaction.categoryId,
          amount: newTransaction.amount,
          type: newTransaction.type,
          description: newTransaction.description,
          date: newTransaction.date,
        });
      }

      await loadTransactions(filters.startDate, filters.endDate);
      setShowForm(false);
      resetTransactionForm();
    } catch (error) {
      console.error("Error guardando transacción:", error);
      alert("Error al guardar la transacción.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    resetTransactionForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta transacción?")) return;

    setLoading(true);
    try {
      await transactionService.deleteTransaction(id);
      await loadTransactions(filters.startDate, filters.endDate);
    } catch (error) {
      console.error("Error eliminando transacción:", error);
      alert("Error al eliminar la transacción.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRecurring = async (id) => {
    if (!window.confirm("¿Cancelar esta transacción recurrente? No se generarán más cargos automáticos.")) return;

    setLoading(true);
    try {
      await transactionService.deleteRecurringTransaction(id);
      await loadRecurringRules();
    } catch (error) {
      console.error("Error cancelando recurrencia:", error);
      alert("Error al cancelar la transacción recurrente.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <Navigate to="/" replace />;

  return (
    <>
      {loading && <CustomSpinner />}
      <div className="min-h-[91vh] banner-1 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-slate-900/80 p-4 shadow-2xl shadow-black/20 sm:p-6">
          <h1 className="mb-4 text-2xl font-semibold">Transacciones</h1>

          <div className="mb-4 grid gap-3 md:grid-cols-3">
            <label className="text-sm text-slate-300 min-w-0">
              <span className="mb-2 block">Fecha inicio</span>
              <div className="w-full overflow-hidden rounded-xl">
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full min-w-0 max-w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                />
              </div>
            </label>
            <label className="text-sm text-slate-300 min-w-0">
              <span className="mb-2 block">Fecha fin</span>
              <div className="w-full overflow-hidden rounded-xl">
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full min-w-0 max-w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"

                />
              </div>
            </label>
            <div className="flex flex-col gap-2 md:justify-end">
              <button
                onClick={handleFilter}
                className="rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-2 text-sm font-medium text-sky-300 transition hover:bg-sky-500/20"
              >
                Filtrar
              </button>
              <button
                onClick={handleOpenCreateForm}
                className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-300 transition hover:bg-emerald-500/20"
              >
                + Nueva transacción
              </button>
            </div>
          </div>

          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6 backdrop-blur-sm">
              <div className="w-full max-w-4xl rounded-3xl border border-white/10 bg-slate-900 p-4 shadow-2xl shadow-black/30 sm:p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">{editingTransactionId ? "Editar transacción" : "Nueva transacción"}</h2>
                  <button onClick={handleCancel} className="rounded-full p-2 text-slate-300 transition hover:bg-white/10">✕</button>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <label className="text-sm text-slate-300">
                    <span className="mb-2 block">Tipo</span>
                    <select
                      value={newTransaction.type}
                      onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value, categoryId: "", categoryName: "" })}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                    >
                      <option value="expense">Gasto</option>
                      <option value="income">Ingreso</option>
                    </select>
                  </label>
                  <label className="text-sm text-slate-300">
                    <span className="mb-2 block">Categoría</span>
                    <select
                      value={newTransaction.categoryId}
                      onChange={(e) => {
                        const selected = categories.find((category) => category.id === e.target.value);
                        setNewTransaction({ ...newTransaction, categoryId: e.target.value, categoryName: selected?.name || "" });
                      }}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                    >
                      <option value="">Seleccionar...</option>
                      {filteredCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm text-slate-300">
                    <span className="mb-2 block">Monto</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="amount"
                      value={newTransaction.amount}
                      onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                    />
                  </label>
                  <label className="text-sm text-slate-300 md:col-span-2 xl:col-span-1">
                    <span className="mb-2 block">Descripción</span>
                    <input
                      type="text"
                      name="description"
                      value={newTransaction.description}
                      onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                    />
                  </label>
                  <label className="text-sm text-slate-300 min-w-0">
                    <span className="mb-2 block">{newTransaction.isRecurring ? "Fecha de inicio" : "Fecha"}</span>
                    <div className="w-full overflow-hidden rounded-xl">
                      <input
                        type="date"
                        name="date"
                        value={newTransaction.date}
                        onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                        className="w-full min-w-0 max-w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                      />
                    </div>
                  </label>
                </div>

                {!editingTransactionId && (
                  <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={newTransaction.isRecurring}
                      onChange={(e) => setNewTransaction({ ...newTransaction, isRecurring: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-900"
                    />
                    Programar transacción recurrente
                  </label>)
                }

                {newTransaction.isRecurring && (
                  <div className="mt-4 max-w-sm">
                    <label className="text-sm text-slate-300">
                      <span className="mb-2 block">Repetir</span>
                      <select
                        value={newTransaction.frequency}
                        onChange={(e) => setNewTransaction({ ...newTransaction, frequency: e.target.value })}
                        className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                      >
                        <option value="daily">Cada día</option>
                        <option value="weekly">Cada semana</option>
                        <option value="biweekly">Cada dos semanas</option>
                        <option value="monthly">Cada mes</option>
                      </select>
                    </label>
                    <div className="mt-3 rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-sm text-slate-300">
                      Se creará un cargo automático {FREQUENCY_LABELS[newTransaction.frequency].toLowerCase()}, a partir de la fecha indicada, hasta que canceles la recurrencia.
                    </div>
                  </div>
                )}

                <div className="mt-5 flex gap-2">
                  <button onClick={handleSave} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-600">
                    {editingTransactionId ? "Actualizar" : "Guardar"}
                  </button>
                  <button onClick={handleCancel} className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-300 transition hover:bg-slate-800">
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {recurringRules.length > 0 && (
            <div className="mb-6 rounded-2xl border border-white/10 bg-slate-950/60 p-4">
              <h2 className="mb-3 text-lg font-semibold">Transacciones recurrentes activas</h2>
              <div className="flex flex-col gap-2">
                {recurringRules.map((rule) => (
                  <div key={rule.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-slate-900/70 px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${rule.type === "income" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                        {rule.type === "income" ? "Ingreso" : "Gasto"}
                      </span>
                      <span className="text-sm text-slate-200">{rule.description || "Sin descripción"}</span>
                      <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-300">
                        {FREQUENCY_LABELS[rule.frequency] || rule.frequency}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-300">
                      <span className="font-semibold text-white">{formatCurrency(rule.amount)}</span>
                      <span>Próximo: {formatDate(rule.nextRunDate)}</span>
                      <button onClick={() => handleCancelRecurring(rule.id)} className="rounded-lg border border-rose-500/30 p-2 text-rose-300 transition hover:bg-rose-500/10" title="Cancelar recurrencia">
                        <Trash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {transactions.length === 0 ? (
            <div className="rounded-2xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-400">
              No hay transacciones en este período
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="min-w-[720px] w-full divide-y divide-white/10 text-sm">
                <thead className="bg-slate-800/80 text-left text-slate-300">
                  <tr>
                    <th className="px-3 py-3">Fecha</th>
                    <th className="px-3 py-3">Descripción</th>
                    <th className="px-3 py-3">Categoría</th>
                    <th className="px-3 py-3">Tipo</th>
                    <th className="px-3 py-3 text-right">Monto</th>
                    <th className="px-3 py-3 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 bg-slate-900/50">
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td className="px-3 py-3">
                        {formatDate(tx.date)}
                        {tx.recurringTransactionId && (
                          <span className="ml-2 rounded-full bg-slate-800 px-2 py-0.5 text-[11px] text-slate-400" title="Generada automáticamente">
                            auto
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">{tx.description || "-"}</td>
                      <td className="px-3 py-3">{tx.categoryName || "-"}</td>
                      <td className="px-3 py-3">{tx.type === "income" ? "Ingreso" : "Gasto"}</td>
                      <td className={`px-3 py-3 text-right font-semibold ${tx.type === "income" ? "text-emerald-400" : "text-rose-400"}`}>
                        {tx.type === "expense" ? "-" : "+"}
                        {formatCurrency(tx.amount)}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => handleEditTransaction(tx)} className="rounded-lg border border-sky-500/30 p-2 text-sky-300 transition hover:bg-sky-500/10" title="Editar">
                            <Pencil />
                          </button>
                          <button onClick={() => handleDelete(tx.id)} className="rounded-lg border border-rose-500/30 p-2 text-rose-300 transition hover:bg-rose-500/10" title="Eliminar">
                            <Trash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}